import React from 'react';
import {RaceCarGroupItem, RaceCarRecord} from '../result.type';
import {getRacecarGroupList, getRacecarHistory} from '../result-service';
import Spin from '@/components/basic/spin';
import theme from '@/style';
import {ScrollView, StyleSheet, View} from 'react-native';
import CarItem from './result-item';
import Text from '@/components/basic/text';
import {useTranslation} from 'react-i18next';
import NoData from '@/components/basic/error-pages/no-data';
import RacecarDraw from './draw';
import ResultTab from '../components/result-tab/result-tab';
import ShowMoreButton from '../components/show-more-button';

const pageSize = 10;
const getGroupMinute = (text: string) =>
  Number.parseFloat(text.replace(/[^0-9.]/g, ''));

const Racecar = ({isActive}: {isActive?: boolean}) => {
  const {t} = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const [firstLoad, setFirstLoad] = React.useState(true);
  const [groupList, setGroupList] = React.useState<RaceCarGroupItem[]>([]);
  const [cycleTabKey, setCycleTabKey] = React.useState<string>('');
  const [list, setList] = React.useState<RaceCarRecord[]>([]);
  const currentPage = React.useRef(1);
  const hasMores = React.useRef(true);
  const listRef = React.useRef<RaceCarRecord[]>([]);
  const loadingMoreRef = React.useRef(false);
  const scrollRef = React.useRef<ScrollView>(null);
  const groupRequestRef = React.useRef(0);
  const historyRequestRef = React.useRef(0);

  const resetHistoryState = (clearList = true) => {
    currentPage.current = 1;
    hasMores.current = true;
    loadingMoreRef.current = false;
    listRef.current = [];
    if (clearList) {
      setList([]);
    }
  };

  const getGroups = async () => {
    const requestId = ++groupRequestRef.current;
    try {
      setLoading(true);
      const group = ((await getRacecarGroupList()) || []).sort((a, b) => {
        return getGroupMinute(a.text) - getGroupMinute(b.text);
      });
      if (requestId !== groupRequestRef.current) {
        return;
      }
      setGroupList(group);
      if (group.length > 0) {
        setCycleTabKey(group[0].groupName);
      } else {
        setFirstLoad(false);
      }
    } catch (_error) {
      if (requestId === groupRequestRef.current) {
        setFirstLoad(false);
      }
    } finally {
      if (requestId === groupRequestRef.current) {
        setLoading(false);
      }
    }
  };

  React.useEffect(() => {
    if (isActive) {
      setFirstLoad(true);
      setGroupList([]);
      setCycleTabKey('');
      resetHistoryState();
      scrollRef.current?.scrollTo({y: 0, animated: false});
      getGroups().then();
    }
  }, [isActive]);

  const getHistory = async (pageNo = 1, groupName = '', loadMore = false) => {
    const requestId = ++historyRequestRef.current;
    try {
      if (!loadMore) {
        setLoading(true);
      }
      const res = await getRacecarHistory({
        pageNo,
        groupName,
        size: pageSize,
      });
      if (requestId !== historyRequestRef.current) {
        return;
      }
      if (res) {
        const currentList = res.records || [];
        const nextList = loadMore
          ? listRef.current.concat(currentList)
          : currentList;
        listRef.current = nextList;
        setList(nextList);
        hasMores.current = pageNo < (res.totalPage || 0);
      }
    } catch (_error) {
      if (loadMore && requestId === historyRequestRef.current) {
        currentPage.current = Math.max(1, currentPage.current - 1);
      }
    } finally {
      if (requestId === historyRequestRef.current) {
        setFirstLoad(false);
        setLoading(false);
        loadingMoreRef.current = false;
      }
    }
  };

  React.useEffect(() => {
    if (cycleTabKey && isActive) {
      setFirstLoad(true);
      resetHistoryState();
      getHistory(1, cycleTabKey).then();
    }
  }, [cycleTabKey, isActive]);

  const renderTabLabel = (text: string) => {
    const minute = getGroupMinute(text);
    return Number.isFinite(minute) ? `${minute}` : text.split('min')[0].trim();
  };

  const drawLabel = React.useMemo(() => {
    const currentGroup = groupList.find(item => item.groupName === cycleTabKey);
    return currentGroup
      ? `${renderTabLabel(currentGroup.text)} ${t('home.label.minute')}`
      : '';
  }, [cycleTabKey, groupList, t]);

  const handleMore = () => {
    if (loading || !hasMores.current || !cycleTabKey) {
      return;
    }
    currentPage.current += 1;
    getHistory(currentPage.current, cycleTabKey, true).then();
  };

  return (
    <Spin
      loading={loading}
      style={[theme.flex.flex1, theme.background.lightGrey]}>
      <ScrollView
        ref={scrollRef}
        stickyHeaderIndices={[1]}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}>
        <View>
          {isActive && !loading ? (
            <RacecarDraw cycleTabKey={cycleTabKey} drawLabel={drawLabel} />
          ) : (
            <View />
          )}
        </View>
        <ResultTab
          selectKey={cycleTabKey}
          onSelectKey={key => {
            scrollRef.current?.scrollTo({y: 0, animated: false});
            setCycleTabKey(key);
          }}
          tabs={groupList.map(item => {
            return {
              label: renderTabLabel(item.text),
              key: item.groupName,
            };
          })}
          textWrapClassName="text-[#2B3240]"
          minuteTextClassName="text-[#5C6575]"
        />
        {list.length > 0 ? (
          <View style={styles.tableWrap}>
            <View style={styles.tableHeader}>
              <View
                style={[
                  theme.flex.row,
                  theme.flex.between,
                  styles.tableHeaderInner,
                ]}>
                <Text style={styles.tableHeaderText}>
                  {t('common.label.table.betId').toUpperCase()}
                </Text>
                <Text style={styles.tableHeaderText}>
                  {t('common.label.table.resultBig').toUpperCase()}
                </Text>
              </View>
            </View>
            {list.map((item, _i) => (
              <CarItem
                key={`${item.roundNum}-${_i}`}
                cars={item.cars}
                roundNum={item.roundNum}
              />
            ))}
            {hasMores.current ? (
              <View style={styles.moreWrap}>
                <ShowMoreButton
                  onPress={handleMore}
                  label={t('home.label.showMore')}
                />
              </View>
            ) : null}
          </View>
        ) : !firstLoad && !loading ? (
          <NoData />
        ) : null}
      </ScrollView>
    </Spin>
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    paddingBottom: 72,
  },
  tableWrap: {
    marginHorizontal: 12,
    marginTop: 8,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
  },
  tableHeader: {
    backgroundColor: '#E9ECF5',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  tableHeaderInner: {
    alignItems: 'center',
  },
  tableHeaderText: {
    color: '#697181',
    fontSize: 12,
    lineHeight: 14,
    fontWeight: '600',
  },
  moreWrap: {
    backgroundColor: '#FFFFFF',
    padding: 12,
  },
});

export default Racecar;
